import { useState } from 'react';
import { Play, Save, Trash2, Database, Table, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { supabase } from '../../lib/supabase';

interface QueryResult {
  data: any[] | null;
  error: string | null;
  executionTime: number;
}

const PREDEFINED_QUERIES = {
  facilities: {
    name: 'المرافق (Facilities)',
    queries: [
      { label: 'عرض جميع المرافق', sql: 'SELECT * FROM facilities ORDER BY created_at DESC;' },
      { label: 'إضافة مرفق جديد', sql: `INSERT INTO facilities (id, name, name_en, icon, created_at)
VALUES (gen_random_uuid(), 'اسم المرفق', 'facility_name', 'Building2', NOW());` },
      { label: 'تحديث مرفق', sql: `UPDATE facilities 
SET name = 'اسم جديد', name_en = 'new_name' 
WHERE id = ' facility-id-here ';` },
      { label: 'حذف مرفق', sql: `DELETE FROM facilities WHERE id = ' facility-id-here ';` },
    ]
  },
  categories: {
    name: 'التصنيفات (Categories)',
    queries: [
      { label: 'عرض جميع التصنيفات', sql: 'SELECT * FROM categories ORDER BY created_at DESC;' },
      { label: 'إضافة تصنيف جديد', sql: `INSERT INTO categories (id, name, name_en, icon, created_at)
VALUES (gen_random_uuid(), 'اسم التصنيف', 'category_name', 'Building2', NOW());` },
    ]
  },
  properties: {
    name: 'العقارات (Properties)',
    queries: [
      { label: 'عرض جميع العقارات', sql: 'SELECT id, title, price, location, category, type, created_at FROM properties ORDER BY created_at DESC LIMIT 50;' },
      { label: 'عقارات للبيع', sql: `SELECT * FROM properties WHERE type = 'sale' ORDER BY created_at DESC;` },
      { label: 'عقارات للإيجار', sql: `SELECT * FROM properties WHERE type = 'rent' ORDER BY created_at DESC;` },
    ]
  },
  nearby: {
    name: 'الأماكن القريبة (Nearby)',
    queries: [
      { label: 'عرض جميع الأماكن', sql: 'SELECT * FROM nearby_places ORDER BY created_at DESC;' },
    ]
  }
};

export default function AdminSQLEditor() {
  const [sql, setSql] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedQueries, setSavedQueries] = useState<{name: string, sql: string}[]>([]);
  const [activeTab, setActiveTab] = useState<'facilities' | 'categories' | 'properties' | 'nearby'>('facilities');

  const executeQuery = async () => {
    if (!sql.trim()) return;
    
    setLoading(true);
    const startTime = performance.now();
    
    try {
      // Try to use Supabase's RPC for SQL execution
      const { data, error } = await supabase.rpc('execute_sql', { query: sql });
      
      const executionTime = performance.now() - startTime;
      
      if (error) {
        setResult({
          data: null,
          error: 'يجب إنشاء stored procedure "execute_sql" في Supabase لتنفيذ SQL مباشرة. استخدم الصفحات المخصصة أو Supabase Dashboard.',
          executionTime
        });
      } else {
        setResult({
          data,
          error: null,
          executionTime
        });
      }
    } catch (err: any) {
      setResult({
        data: null,
        error: err.message || 'حدث خطأ أثناء تنفيذ الاستعلام',
        executionTime: performance.now() - startTime
      });
    } finally {
      setLoading(false);
    }
  };

  const saveQuery = () => {
    if (!sql.trim()) return;
    const name = prompt('أدخل اسم للاستعلام المحفوظ:');
    if (name) {
      setSavedQueries(prev => [...prev, { name, sql }]);
    }
  };

  const loadQuery = (querySql: string) => {
    setSql(querySql);
  };

  const clearResults = () => {
    setResult(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">محرر SQL</h1>
          <p className="text-gray-600">تنفيذ استعلامات SQL مباشرة على قاعدة البيانات</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={clearResults}
            disabled={!result}
          >
            <Trash2 className="w-4 h-4 ml-2" />
            مسح النتائج
          </Button>
          <Button
            variant="outline"
            onClick={saveQuery}
            disabled={!sql.trim()}
          >
            <Save className="w-4 h-4 ml-2" />
            حفظ الاستعلام
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar with Predefined Queries */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center gap-2 mb-4">
              <Database className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-bold text-gray-800">الجداول</h2>
            </div>
            
            <div className="space-y-2">
              {Object.entries(PREDEFINED_QUERIES).map(([key, section]) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key as any)}
                  className={`w-full text-right px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === key 
                      ? 'bg-[#D4AF37] text-white' 
                      : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  {section.name}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center gap-2 mb-4">
              <Table className="w-5 h-5 text-[#D4AF37]" />
              <h2 className="font-bold text-gray-800">استعلامات جاهزة</h2>
            </div>
            
            <div className="space-y-2">
              {PREDEFINED_QUERIES[activeTab].queries.map((query, idx) => (
                <button
                  key={idx}
                  onClick={() => loadQuery(query.sql)}
                  className="w-full text-right px-3 py-2 rounded-lg text-sm hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  {query.label}
                </button>
              ))}
            </div>
          </div>

          {savedQueries.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="font-bold text-gray-800 mb-4">استعلامات محفوظة</h2>
              <div className="space-y-2">
                {savedQueries.map((query, idx) => (
                  <button
                    key={idx}
                    onClick={() => loadQuery(query.sql)}
                    className="w-full text-right px-3 py-2 rounded-lg text-sm hover:bg-gray-100 text-gray-700 transition-colors"
                  >
                    {query.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Main Editor */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="flex items-center justify-between mb-3">
              <label className="font-medium text-gray-700">استعلام SQL</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">Ctrl+Enter للتنفيذ</span>
              </div>
            </div>
            
            <textarea
              value={sql}
              onChange={(e) => setSql(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === 'Enter') {
                  e.preventDefault();
                  executeQuery();
                }
              }}
              placeholder="-- اكتب استعلام SQL هنا
-- مثال: SELECT * FROM facilities;"
              className="w-full h-64 px-4 py-3 border rounded-lg font-mono text-sm direction-ltr text-left bg-gray-50 focus:bg-white transition-colors"
              dir="ltr"
            />
            
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                {sql.length > 0 && `${sql.length} حرف`}
              </div>
              <Button
                onClick={executeQuery}
                disabled={loading || !sql.trim()}
                className="bg-[#D4AF37] hover:bg-[#B8960C] text-white"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 ml-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 ml-2" />
                )}
                تنفيذ الاستعلام
              </Button>
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="bg-white rounded-lg shadow-md p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {result.error ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  <h2 className="font-bold text-gray-800">
                    {result.error ? 'خطأ' : 'النتائج'}
                  </h2>
                </div>
                <span className="text-sm text-gray-500">
                  {result.executionTime.toFixed(2)} ms
                </span>
              </div>

              {result.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                  {result.error}
                </div>
              ) : result.data && result.data.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        {Object.keys(result.data[0]).map((key) => (
                          <th key={key} className="px-4 py-2 text-right text-sm font-medium text-gray-600">
                            {key}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.data.map((row, idx) => (
                        <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {Object.values(row).map((value: any, vidx) => (
                            <td key={vidx} className="px-4 py-2 text-sm text-gray-700">
                              {typeof value === 'object' 
                                ? JSON.stringify(value) 
                                : String(value)
                              }
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-500 text-center py-8">
                  لا توجد نتائج
                </div>
              )}
            </div>
          )}

          {/* Help Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-bold text-blue-800 mb-2">ملاحظات هامة:</h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>يتطلب تنفيذ SQL إعداد stored procedure في Supabase</li>
              <li>للاستعلامات المعقدة، استخدم <a href="https://app.supabase.com" target="_blank" rel="noopener noreferrer" className="underline">Supabase Dashboard</a></li>
              <li>استخدم الصفحات المخصصة لإدارة المرافق والتصنيفات بشكل أسهل</li>
              <li>تأكد من عمل نسخة احتياطية قبل تنفيذ عمليات DELETE أو UPDATE</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
